import { useState, useEffect } from 'react'
import { List, Clock, CheckCircle, XCircle, AlertCircle, Trash2, ChevronRight } from 'lucide-react'
import { listJobs, deleteJob, type JobListItem } from '../lib/api'
import { formatCurrency, formatDate } from '../lib/utils'

interface JobListProps {
  onSelectJob: (jobId: string) => void
  selectedJobId?: string
  refreshTrigger?: number
}

const STATUS_CONFIG = {
  pending: { 
    icon: Clock, 
    class: 'text-gray-400',
    bg: 'bg-gray-500/10',
    border: 'border-gray-500/20'
  },
  running: { 
    icon: null, 
    class: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20'
  },
  completed: { 
    icon: CheckCircle, 
    class: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20'
  },
  failed: { 
    icon: XCircle, 
    class: 'text-red-400',
    bg: 'bg-red-500/10',
    border: 'border-red-500/20'
  },
  cancelled: { 
    icon: AlertCircle, 
    class: 'text-gray-400',
    bg: 'bg-gray-500/10',
    border: 'border-gray-500/20'
  }
}

export function JobList({ onSelectJob, selectedJobId, refreshTrigger }: JobListProps) {
  const [jobs, setJobs] = useState<JobListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState<string | null>(null)

  const fetchJobs = async () => {
    try {
      const data = await listJobs()
      setJobs(data)
    } catch {
      console.error('Failed to fetch jobs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchJobs()
    const interval = setInterval(fetchJobs, 5000)
    return () => clearInterval(interval)
  }, [refreshTrigger])

  const handleDelete = async (e: React.MouseEvent, jobId: string) => {
    e.stopPropagation()
    
    if (!confirm('Are you sure you want to delete this job?')) return
    
    setDeleting(jobId)
    try {
      await deleteJob(jobId)
      setJobs(prev => prev.filter(j => j.job_id !== jobId))
    } catch {
      alert('Failed to delete job')
    } finally {
      setDeleting(null)
    }
  }

  if (loading) {
    return (
      <div>
        <h2 className="section-title mb-4">
          <List className="w-5 h-5 text-primary-400" />
          Jobs
        </h2>
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="section-title mb-4">
        <List className="w-5 h-5 text-primary-400" />
        Jobs
        {jobs.length > 0 && (
          <span className="text-sm font-normal text-gray-500 ml-1">
            ({jobs.length})
          </span>
        )}
      </h2>

      {jobs.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-surface-800/50 flex items-center justify-center">
            <List className="w-6 h-6 text-gray-600" />
          </div>
          <p className="text-gray-400 text-sm">No jobs yet</p>
          <p className="text-gray-500 text-xs mt-1">Upload files to get started</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
          {jobs.map(job => {
            const statusConfig = STATUS_CONFIG[job.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pending
            const StatusIcon = statusConfig.icon
            const isSelected = selectedJobId === job.job_id
            
            return (
              <div
                key={job.job_id}
                onClick={() => onSelectJob(job.job_id)}
                className={`group relative p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                  isSelected
                    ? 'bg-primary-500/10 border border-primary-500/30'
                    : 'bg-surface-800/30 border border-transparent hover:bg-surface-800/50 hover:border-white/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${statusConfig.bg} ${statusConfig.border} border`}>
                    {job.status === 'running' ? (
                      <div className="w-4 h-4 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
                    ) : StatusIcon ? (
                      <StatusIcon className={`w-4 h-4 ${statusConfig.class}`} />
                    ) : null}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">{job.name}</span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig.bg} ${statusConfig.class}`}>
                          {job.status}
                        </span>
                        {job.status !== 'running' && (
                          <button
                            onClick={(e) => handleDelete(e, job.job_id)}
                            disabled={deleting === job.job_id}
                            className="p-1 text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            {deleting === job.job_id ? (
                              <div className="w-3.5 h-3.5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                        <ChevronRight className={`w-4 h-4 transition-transform ${isSelected ? 'text-primary-400 translate-x-0.5' : 'text-gray-600'}`} />
                      </div>
                    </div>

                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{job.completed}/{job.total_samples} samples</span>
                      <span className="text-amber-400/80">{formatCurrency(job.cost_usd)}</span>
                      <span className="hidden sm:inline">{formatDate(job.created_at)}</span>
                    </div>

                    {job.status === 'running' && (
                      <div className="mt-2">
                        <div className="h-1 bg-surface-700 rounded-full overflow-hidden">
                          <div
                            className="h-full progress-bar transition-all duration-500"
                            style={{ width: `${job.progress_percent}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-gray-500 mt-0.5 inline-block">
                          {job.progress_percent.toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
